<template>
    <TransitionRoot appear :show="scanner.isOpen" as="template">
        <Dialog as="div" @close="scanner.closeModal()" class="relative z-10">
            <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0" enter-to="opacity-100"
                leave="duration-200 ease-in" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-black bg-opacity-25" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 text-center">
                    <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100" leave="duration-200 ease-in" leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95">
                        <DialogPanel
                            class="w-full max-w-lg transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                            <DialogTitle as="h3"
                                class="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between">
                                <div class="flex items-center">
                                    ⚡ {{ dataLocale.data.title }}
                                </div>
                                <div class="cursor-pointer" @click="scanner.closeModal()">
                                    <XIcon size="20" />
                                </div>
                            </DialogTitle>
                            <div v-if="scanner.response.length" class="max-h-[600px] overflow-y-auto overflow-x-hidden">
                                <div v-for="res in scanner.response" class="mb-2">
                                    <div class="mt-4 p-4 bg-gray-100 rounded-md max-w-full overflow-x-auto">
                                        <pre class="text-sm text-gray-500">{{ res.content }}</pre>
                                    </div>
                                    <div class="mt-4 flex items-center gap-2">
                                        <button type="button" v-if="res.type"
                                            class="flex items-center gap-1 justify-center rounded-md border border-transparent bg-white px-4 py-2 text-sm font-medium text-black drop-shadow-md hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-aqua focus-visible:ring-offset-2"
                                            @click="eventClickType(res.type, res.content)">
                                            <template v-if="res.type == 'link'">
                                                <ExternalLinkIcon size="18" stroke-width="1" /> Redirect
                                            </template>
                                            <template v-if="res.type == 'rel'">
                                                Tel
                                            </template>
                                            <template v-if="res.type == 'vcard'">
                                                <IdIcon size="18" stroke-width="1" /> Download .vcf
                                            </template>
                                            <template v-if="res.type == 'geo'">
                                                <MapPinIcon size="18" stroke-width="1" /> Geo
                                            </template>
                                        </button>
                                        <button type="button" @click="CopyContext(res, res.content)"
                                            class="inline-flex gap-1 items-center justify-center rounded-md border border-transparent bg-success px-4 py-2 text-sm font-medium drop-shadow-md text-white hover:bg-success/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2">
                                            <CopyIcon size="18" stroke-width="2" />
                                            <template v-if="res.copied">
                                                Copied
                                            </template>
                                            <template v-else>
                                                Copy
                                            </template>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <template v-else>
                                <div class=" mt-4">
                                    <MoodConfuzedIcon size="32" />
                                </div>
                                <div class="text-sm text-gray-500 py-2" v-html="dataLocale.data.message_error">

                                </div>
                                <button type="button" @click="scanner.closeModal()"
                                    class="inline-flex mt-4 gap-1 items-center justify-center rounded-md border border-transparent bg-gray-300 px-4 py-2 text-sm font-medium drop-shadow-md text-black hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2">
                                    {{ $t('close') }}
                                </button>
                            </template>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script setup lang="ts">
import { AlignRightIcon, CopyIcon, ExternalLinkIcon, IdIcon, MapPinIcon, XIcon, MoodConfuzedIcon } from "vue-tabler-icons"
import { storeToRefs } from 'pinia'
import {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogTitle,
} from '@headlessui/vue'
import { useScanner } from "~~/stores/scanner";
import DataLocale from "~~/config/interface/DataLocale";

const dataLocale = new DataLocale([
    {
        locale: 'en',
        title: "Decoding Result",
        message_error: "The content of the QR code in this picture <b>cannot be recognized</b>"
    },
    {
        locale: 'vi',
        title: "Kết quả giải mã",
        message_error: "Nội dung của mã QR trong bức ảnh này <b>không thể nhận dạng được</b>"
    }
    ,
    {
        locale: 'es',
        title: "Resultado de decodificación",
        message_error: "El contenido del código QR en esta imagen <b>no puede ser reconocido</b>"
    }
    ,
    {
        locale: 'fr',
        title: "Résultat de décodage",
        message_error: "Le contenu du code QR dans cette image <b>ne peut pas être reconnu</b>"
    }
    ,
    {
        locale: 'it',
        title: "Risultato di decodifica",
        message_error: "Il contenuto del codice QR in questa immagine <b>non può essere riconosciuto</b>"
    }
    ,
    {
        locale: 'nl',
        title: "Decoderingsresultaat",
        message_error: "De inhoud van de QR-code in deze afbeelding <b>kan niet worden herkend</b>"
    }
    ,
    {
        locale: 'ja',
        title: "解読結果",
        message_error: "この画像のQRコードの内容<b>を認識できません</b>"
    }
    ,
    {
        locale: 'zh',
        title: "解码结果",
        message_error: "无法识别此图片中的QR码<b>内容</b>"
    }
    ,
    {
        locale: 'pt',
        title: "Resultado da decodificação",
        message_error: "O conteúdo do código QR nesta imagem <b>não pode ser reconhecido</b>"
    },
    {
        "locale": "de",
        "title": "Dekodierungsergebnis",
        "message_error": "Der Inhalt des QR-Codes auf diesem Bild <b>kann nicht erkannt werden</b>"
    }
])

const scanner = useScanner()
const { response } = storeToRefs(scanner)
const utils = useUtils()
watch(response, (v) => {
    validateType(v)
})

interface ResponseScanner {
    content: string;
    name?: string | undefined;
    file?: string | undefined;
    type?: string
    copied?: boolean
}
const validateType = (responses: Array<ResponseScanner>) => {

    const typeInText = (txt: string): string => {
        if (txt) {
            txt = txt.toLocaleLowerCase()
        }
        if (txt.includes("begin:vcard")) {
            return "vcard";
        }
        if (txt.includes("tel:")) {
            return "tel";
        }
        if (txt.includes("geo:")) {
            return "geo";
        }
        if (/^(https?|chrome):\/\/[^\s$.?#].[^\s]*$/gm.test(txt)) {
            return "link";
        }
        return "";
    }

    responses.forEach(item => {
        item.type = typeInText(item.content)
        item.copied = false;
    })
}

const CopyContext = (res: ResponseScanner, text: string) => {
    res.copied = true;
    utils.copyText(text)
    setTimeout(() => {
        res.copied = false
    }, 500)
}

const downloadVcard = (text: string) => {
    const url = window.URL.createObjectURL(
        new Blob([text], { type: "text/x-vcard" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "vcard_" + Date.now() + ".vcf");
    document.body.appendChild(link);
    link.click();
}

const { isMobile } = useDevice();

const googleMap = (text: string) => {
    const regex = /geo\:([0-9]*\.\d+)+\,([0-9]*\.\d+)\?/gm;
    let m;
    m = regex.exec(text);
    try {
        if (isMobile) {
            utils.redirectUrl(text)
        } else {
            if (Array.isArray(m)) {
                var url = `https://www.google.com/maps/search/?api=1&query=${m[1]},${m[2]}`;
                utils.redirectUrl(url)
            } else {
                utils.redirectUrl(text)
            }
        }
    } catch (e) { }
}

const eventClickType = (type: string | undefined, content: string) => {
    switch (type) {
        case 'link':
        case 'tel':
            utils.redirectUrl(content)
            break;
        case 'vcard':
            downloadVcard(content)
            break;
        case 'geo':
            googleMap(content)
            break;
    }
}

</script>